/**
 * The billing success page's wait (W4 §6): Stripe's redirect grants nothing, so the page stays
 * pending until the verified webhook's purchase shows up — a purchase entry from around this page
 * load, or the balance rising — for at most 60 s. An older purchase (a repeat buyer) never counts.
 */

import { useEffect, useState } from 'react';
import { billingAPI } from '../services/api';

const MAX_WAIT_MS = 60_000;
const POLL_INTERVAL_MS = 2_000;
/** The webhook can land before Stripe's redirect finishes loading this page. */
const WEBHOOK_HEAD_START_MS = 2 * 60_000;

export type PurchaseStatus = 'pending' | 'success' | 'timeout';

export function usePurchaseConfirmation(): { status: PurchaseStatus; credits: number | null } {
  const [status, setStatus] = useState<PurchaseStatus>('pending');
  const [credits, setCredits] = useState<number | null>(null);

  useEffect(() => {
    const loadedAt = Date.now();
    const since = loadedAt - WEBHOOK_HEAD_START_MS;
    let firstBalanceMilli: number | null = null;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    async function poll() {
      try {
        const [activity, balance] = await Promise.all([
          billingAPI.getActivity({ kind: 'purchase', limit: 5 }),
          billingAPI.getBalance(),
        ]);
        if (cancelled) return;
        firstBalanceMilli ??= balance.balanceMilli;
        const landed = activity.entries.some((e) => e.kind === 'purchase' && new Date(e.at).getTime() > since)
          || balance.balanceMilli > firstBalanceMilli;
        if (landed) {
          setCredits(Number((Math.max(0, balance.availableMilli) / 1000).toFixed(2)));
          setStatus('success');
          return;
        }
      } catch {
        // Ignore — transient auth or network
      }
      if (cancelled) return;
      if (Date.now() - loadedAt >= MAX_WAIT_MS) {
        setStatus('timeout');
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    }
    poll();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  return { status, credits };
}
